import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  ArrowLeft,
  TrendingUp,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

interface MonitoringData {
  timestamp: string;
  sessions: any[];
  healthStats: {
    total: number;
    healthy: number;
    failed: number;
    unknown: number;
  };
  recoveryLog: any[];
  recoveryStats: {
    total: number;
    successful: number;
    failed: number;
    avgDuration: number;
  };
  queueStatusCounts: {
    pending: number;
    processing: number;
    failed: number;
  };
  syncStatus: any[];
  syncStats: {
    total: number;
    successful: number;
    failed: number;
    totalMessagesSynced: number;
  };
}

export function SystemMonitor() {
  const navigate = useNavigate();
  const [data, setData] = useState<MonitoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = async () => {
    try {
      const response = await fetch('/api/admin/monitor');
      if (!response.ok) throw new Error('Failed to fetch monitoring data');
      const json = await response.json();
      setData(json);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#5B8E87] mx-auto" />
          <p className="mt-4 text-gray-600">Loading monitoring data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-center">Error Loading Data</h2>
          <p className="mt-2 text-gray-600 text-center">{error}</p>
          <button
            onClick={() => {
              setLoading(true);
              fetchData();
            }}
            className="mt-6 w-full px-4 py-2 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4F7A74]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const healthPercentage = data.healthStats.total > 0
    ? Math.round((data.healthStats.healthy / data.healthStats.total) * 100)
    : 0;

  const recoverySuccessRate = data.recoveryStats.total > 0
    ? Math.round((data.recoveryStats.successful / data.recoveryStats.total) * 100)
    : 0;

  const syncSuccessRate = data.syncStats.total > 0
    ? Math.round((data.syncStats.successful / data.syncStats.total) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">System Monitor</h1>
                <p className="text-sm text-gray-600">WhatsApp Message Recovery System</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded border-gray-300 text-[#5B8E87] focus:ring-[#5B8E87]"
                />
                Auto-refresh (30s)
              </label>
              <button
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4F7A74]"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatsCard
            title="Session Health"
            value={`${healthPercentage}%`}
            subtitle={`${data.healthStats.healthy}/${data.healthStats.total} healthy`}
            icon={<Activity className="w-6 h-6" />}
            color={healthPercentage >= 90 ? 'green' : healthPercentage >= 70 ? 'yellow' : 'red'}
          />
          <StatsCard
            title="Recovery Success"
            value={`${recoverySuccessRate}%`}
            subtitle={`${data.recoveryStats.successful}/${data.recoveryStats.total} successful`}
            icon={<TrendingUp className="w-6 h-6" />}
            color={recoverySuccessRate >= 90 ? 'green' : recoverySuccessRate >= 70 ? 'yellow' : 'red'}
          />
          <StatsCard
            title="Sync Queue"
            value={data.queueStatusCounts.pending.toString()}
            subtitle={`${data.queueStatusCounts.processing} processing`}
            icon={<Clock className="w-6 h-6" />}
            color="blue"
          />
          <StatsCard
            title="Messages Synced"
            value={data.syncStats.totalMessagesSynced.toLocaleString()}
            subtitle={`${syncSuccessRate}% success rate`}
            icon={<CheckCircle className="w-6 h-6" />}
            color="green"
          />
        </div>

        {/* Sessions Table */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">WhatsApp Sessions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Connection</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Health Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Health Check</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Webhook</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failures</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.sessions.map((session) => (
                  <tr key={session.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{session.users?.name || 'Unknown'}</div>
                        <div className="text-sm text-gray-500">{session.users?.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {session.connected ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Connected
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Disconnected
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <HealthStatusBadge status={session.health_check_status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {session.last_health_check_at
                        ? new Date(session.last_health_check_at).toLocaleString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {session.last_webhook_received_at
                        ? new Date(session.last_webhook_received_at).toLocaleString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4">
                      {session.consecutive_health_failures > 0 ? (
                        <span className="text-red-600 font-medium">{session.consecutive_health_failures}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recovery Log */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Recovery Attempts (Last 24h)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.recoveryLog.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      No recovery attempts in the last 24 hours
                    </td>
                  </tr>
                ) : (
                  data.recoveryLog.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{log.users?.name || 'Unknown'}</div>
                          <div className="text-gray-500">{log.users?.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {log.recovery_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{log.trigger_reason}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{log.action_taken}</td>
                      <td className="px-6 py-4">
                        {log.success ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Success
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Failed
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {log.duration_ms ? `${(log.duration_ms / 1000).toFixed(1)}s` : 'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsCard({
  title,
  value,
  subtitle,
  icon,
  color,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  color: 'green' | 'yellow' | 'red' | 'blue';
}) {
  const colorClasses = {
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-600',
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
      </div>
      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <p className="text-2xl font-bold text-gray-900 mb-1">{value}</p>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
  );
}

function HealthStatusBadge({ status }: { status?: string }) {
  if (!status || status === 'unknown') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Unknown
      </span>
    );
  }

  if (status === 'healthy') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Healthy
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      <AlertCircle className="w-3 h-3 mr-1" />
      Failed
    </span>
  );
}
